import React, { useCallback, useEffect, useMemo, useState } from 'react';

import DOMPurify from 'dompurify';
import { JwtPayload, jwtDecode } from 'jwt-decode';
import { marked } from 'marked';
import { useSnackbar } from 'notistack';
import auth from '~/auth';

import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import KeyIcon from '@mui/icons-material/Key';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Container,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

import PageHeader from '~/components/Common/Elements/PageHeader';
import { getExperiencesCodexSkillText } from '~/components/Experiences/Home/experienceCodexSkill';
import { getOffersCodexSkillText } from '~/components/Offers/Detail/offerCodexSkill';
import { getToursCodexSkillText } from '~/components/Tours/TourDetails/tourCodexSkill';

type ContentSkill = {
  id: string;
  title: string;
  summary: string;
  getText: () => string;
};

type TokenStatus = {
  expiresAt: Date | null;
  token: string | null;
};

const CONTENT_SKILLS: Array<ContentSkill> = [
  {
    id: 'offers-api',
    title: 'Offers API skill',
    summary:
      'Offer copy, localised content, fine print, images, videos, badges, traveller requirements, and inclusions.',
    getText: getOffersCodexSkillText,
  },
  {
    id: 'tours-api',
    title: 'Tours API skill',
    summary:
      'Tour form resources, options, images, fine print, traveller requirements, schedules, and related content.',
    getText: getToursCodexSkillText,
  },
  {
    id: 'experiences-api',
    title: 'Experiences API skill',
    summary: 'Experience curation content, deal options, additional information, categories, schedules, and media.',
    getText: getExperiencesCodexSkillText,
  },
];

function getApiDetailsText(token: string) {
  return `Luxury Escapes Admin Content API details

API_BASE_URL=${window.configs.API_HOST}
LUX_API_BASE_URL=${window.configs.LUX_API_DOMAIN || window.configs.API_HOST}
ADMIN_BEARER_TOKEN=${token}

Keep ADMIN_BEARER_TOKEN secret. Use it only for this Codex task. These credentials are short-lived and may expire within the hour.

Also paste the Admin URL for the deal, offer, tour, or experience you want edited. The AI can extract the right ID from that URL.`;
}

function getTokenExpiresAt(token: string | null): Date | null {
  if (!token) {
    return null;
  }

  try {
    const { exp } = jwtDecode<JwtPayload>(token);

    if (!exp) {
      return null;
    }

    // Legacy auth stores exp in milliseconds; V2 stores standard JWT seconds.
    return new Date(exp > 9999999999 ? exp : exp * 1000);
  } catch (_error) {
    return null;
  }
}

function formatTokenExpiry(expiresAt: Date | null, now: Date) {
  if (!expiresAt) {
    return 'Token expiry is unavailable.';
  }

  const msUntilExpiry = expiresAt.getTime() - now.getTime();
  const absMinutes = Math.max(0, Math.ceil(Math.abs(msUntilExpiry) / 60000));
  const formattedTime = new Intl.DateTimeFormat(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  }).format(expiresAt);

  if (msUntilExpiry <= 0) {
    return `Current token expired ${absMinutes} ${absMinutes === 1 ? 'minute' : 'minutes'} ago at ${formattedTime}.`;
  }

  if (absMinutes < 60) {
    return `Current token expires in ${absMinutes} ${absMinutes === 1 ? 'minute' : 'minutes'} at ${formattedTime}.`;
  }

  const hours = Math.floor(absMinutes / 60);
  const minutes = absMinutes % 60;
  const duration =
    minutes > 0
      ? `${hours} ${hours === 1 ? 'hour' : 'hours'} ${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`
      : `${hours} ${hours === 1 ? 'hour' : 'hours'}`;

  return `Current token expires in ${duration} at ${formattedTime}.`;
}

async function copyTextToClipboard(text: string) {
  if (!navigator.clipboard?.writeText) {
    throw new Error('Clipboard access is unavailable');
  }

  await navigator.clipboard.writeText(text);
}

function SkillMarkdownPreview({ content }: { content: string }) {
  const theme = useTheme();
  const html = useMemo(() => {
    const raw = marked.parse(content, { async: false, breaks: true, gfm: true });
    return DOMPurify.sanitize(raw);
  }, [content]);

  return (
    <Box
      dangerouslySetInnerHTML={{ __html: html }}
      sx={{
        bgcolor: 'grey.50',
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        color: 'text.primary',
        maxHeight: 560,
        overflow: 'auto',
        px: 3,
        py: 2,
        '& h1': {
          ...theme.typography.h4,
          mt: 0,
          mb: 2,
        },
        '& h2': {
          ...theme.typography.h5,
          mt: 3,
          mb: 1.5,
        },
        '& h3': {
          ...theme.typography.h6,
          mt: 2,
          mb: 1,
        },
        '& p, & li': {
          ...theme.typography.body2,
        },
        '& code': {
          bgcolor: 'action.hover',
          border: 1,
          borderColor: 'divider',
          borderRadius: 0.5,
          fontFamily: 'monospace',
          fontSize: '0.875em',
          px: 0.5,
          py: 0.25,
        },
        '& pre': {
          bgcolor: 'background.paper',
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          overflow: 'auto',
          p: 2,
          '& code': {
            bgcolor: 'transparent',
            border: 0,
            p: 0,
          },
        },
        '& table': {
          borderCollapse: 'collapse',
          my: 2,
          width: '100%',
          '& th, & td': {
            border: 1,
            borderColor: 'divider',
            p: 1,
            textAlign: 'left',
            verticalAlign: 'top',
          },
          '& th': {
            bgcolor: 'action.hover',
          },
        },
      }}
    />
  );
}

export default function ContentToolsPage() {
  const { enqueueSnackbar = () => undefined } = useSnackbar() ?? {};
  const [now, setNow] = useState(() => new Date());
  const [tokenStatus, setTokenStatus] = useState<TokenStatus>({
    expiresAt: getTokenExpiresAt(auth.getState().token),
    token: auth.getState().token,
  });
  const [isRefreshingToken, setIsRefreshingToken] = useState(false);

  const updateTokenStatus = useCallback(async (options: { forceRefresh?: boolean } = {}) => {
    const token = options.forceRefresh
      ? await auth.getFreshAccessToken()
      : (auth.getState().token ?? (await auth.getAccessToken()));

    setTokenStatus({
      expiresAt: getTokenExpiresAt(token),
      token,
    });

    return token;
  }, []);

  useEffect(() => {
    void updateTokenStatus();
  }, [updateTokenStatus]);

  useEffect(() => {
    const interval = window.setInterval(() => {
      setNow(new Date());
    }, 30000);

    return () => {
      window.clearInterval(interval);
    };
  }, []);

  const handleCopyText = useCallback(
    async (text: string, successMessage: string) => {
      try {
        await copyTextToClipboard(text);
        enqueueSnackbar(successMessage, { variant: 'success' });
      } catch (error) {
        enqueueSnackbar(error instanceof Error ? error.message : 'Unable to copy text.', { variant: 'error' });
      }
    },
    [enqueueSnackbar],
  );

  const handleCopyApiDetails = useCallback(async () => {
    try {
      const token = await auth.getAccessToken();

      if (!token) {
        throw new Error('Could not get an Admin API token. Please sign in again.');
      }

      setTokenStatus({
        expiresAt: getTokenExpiresAt(token),
        token,
      });
      await copyTextToClipboard(getApiDetailsText(token));
      enqueueSnackbar('Admin API details copied.', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(error instanceof Error ? error.message : 'Unable to copy Admin API details.', {
        variant: 'error',
      });
    }
  }, [enqueueSnackbar]);

  const handleRefreshToken = useCallback(async () => {
    try {
      setIsRefreshingToken(true);
      const token = await updateTokenStatus({ forceRefresh: true });

      if (!token) {
        throw new Error('Could not refresh the Admin API token. Please sign in again.');
      }

      enqueueSnackbar('Admin API token refreshed.', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(error instanceof Error ? error.message : 'Unable to refresh Admin API token.', {
        variant: 'error',
      });
    } finally {
      setIsRefreshingToken(false);
    }
  }, [enqueueSnackbar, updateTokenStatus]);

  const handleCopyAllSkills = useCallback(() => {
    const allSkillText = CONTENT_SKILLS.map((skill) => skill.getText()).join('\n\n---\n\n');
    void handleCopyText(allSkillText, 'All content API skills copied.');
  }, [handleCopyText]);

  return (
    <Container maxWidth="xl">
      <PageHeader title="AI Tools" />

      <Stack spacing={3} mt={3}>
        <Alert severity="info">
          These skills are just chunks of text that describe the Admin APIs. Copy one skill, combine several into a
          larger skill, or add your own workflow notes and tone-of-voice guidance. Paste the API details separately each
          time you ask Claude or Codex to make a change because the Admin token is short-lived and may expire within an
          hour. Also paste the Admin URL for the deal, offer, tour, or experience you want edited; your AI can extract
          the right ID from that URL.
        </Alert>

        <Stack spacing={1.5}>
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap alignItems="center">
            <Button variant="contained" startIcon={<KeyIcon />} onClick={handleCopyApiDetails}>
              Copy API details
            </Button>
            <Button
              variant="text"
              startIcon={<RefreshIcon />}
              disabled={isRefreshingToken}
              onClick={handleRefreshToken}
            >
              {isRefreshingToken ? 'Refreshing token...' : 'Refresh token now'}
            </Button>
            <Button variant="text" startIcon={<ContentCopyIcon />} onClick={handleCopyAllSkills}>
              Copy all skills
            </Button>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center" color="text.secondary">
            <AccessTimeIcon fontSize="small" />
            <Typography variant="body2">
              {tokenStatus.token
                ? formatTokenExpiry(tokenStatus.expiresAt, now)
                : 'Token expiry will appear after you sign in or copy API details.'}
            </Typography>
          </Stack>
        </Stack>

        <Box>
          <Typography variant="h4" component="h2" gutterBottom>
            Skills
          </Typography>
          <Stack spacing={2}>
            {CONTENT_SKILLS.map((skill) => {
              const skillText = skill.getText();

              return (
                <Accordion key={skill.id} variant="outlined">
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Stack
                      direction={{ xs: 'column', md: 'row' }}
                      spacing={2}
                      justifyContent="space-between"
                      alignItems={{ xs: 'flex-start', md: 'center' }}
                      sx={{ width: '100%', mr: 2 }}
                    >
                      <Box>
                        <Typography variant="h6" component="h3">
                          {skill.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {skill.summary}
                        </Typography>
                      </Box>
                      <Button
                        variant="text"
                        startIcon={<AutoFixHighIcon />}
                        onClick={(event) => {
                          event.stopPropagation();
                          void handleCopyText(skillText, `${skill.title} copied.`);
                        }}
                      >
                        Copy skill
                      </Button>
                    </Stack>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Divider sx={{ mb: 2 }} />
                    <SkillMarkdownPreview content={skillText} />
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </Stack>
        </Box>
      </Stack>
    </Container>
  );
}
